<template>
  <div class="bar-chart">
    <div class="chart-header">
      <div>
        <h3 class="chart-title">{{ title }}</h3>
        <div v-if="subtitle" class="subtitle">{{ subtitle }}</div>
      </div>
      <div v-if="hasSecondary" class="legend">
        <span class="legend-item">
          <span class="legend-dot primary-dot" />
          {{ primaryLabel }}
        </span>
        <span class="legend-item">
          <span class="legend-dot secondary-dot" />
          {{ secondaryLabel }}
        </span>
      </div>
    </div>

    <div v-if="data.length > 0" class="rows">
      <div v-for="item in data" :key="item.label" class="row">
        <div class="row-label" :title="item.label">{{ item.label }}</div>
        <div class="row-track">
          <div
            v-if="item.count > 0"
            class="row-bar primary"
            :style="{ width: getSegmentWidth(item.count) + '%' }"
            :title="`${primaryLabel}: ${item.count}`"
          />
          <div
            v-if="hasSecondary && (item.secondaryCount ?? 0) > 0"
            class="row-bar secondary"
            :style="{ width: getSegmentWidth(item.secondaryCount ?? 0) + '%' }"
            :title="`${secondaryLabel}: ${item.secondaryCount}`"
          />
        </div>
        <div class="row-count">
          <span class="count-primary">{{ item.count }}</span>
          <template v-if="hasSecondary">
            <span class="count-sep">/</span>
            <span class="count-secondary">{{ item.secondaryCount ?? 0 }}</span>
          </template>
        </div>
      </div>
    </div>

    <div v-else class="no-data">
      <p>No data available</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface Props {
  title: string;
  subtitle?: string;
  data: { label: string; count: number; secondaryCount?: number }[];
  color?: string;
  secondaryColor?: string;
  primaryLabel?: string;
  secondaryLabel?: string;
}

const props = withDefaults(defineProps<Props>(), {
  color: '#3b82f6',
  secondaryColor: '#93c5fd',
  primaryLabel: 'Primary',
  secondaryLabel: 'Secondary',
});

const color = computed(() => props.color);
const secondaryColor = computed(() => props.secondaryColor);

// A second color series (e.g. anonymous devices) is opt-in per chart — only
// present once any row carries a secondaryCount.
const hasSecondary = computed(() => props.data.some((item) => item.secondaryCount !== undefined));

// Scaled against the combined (primary + secondary) total so a stacked bar's
// full length represents that row's grand total, same as the single-series
// bar did against its own count.
const maxTotal = computed(() =>
  Math.max(...props.data.map((item) => item.count + (item.secondaryCount ?? 0)), 1),
);

const getSegmentWidth = (count: number): number => {
  if (count <= 0) return 0;
  const percentage = (count / maxTotal.value) * 100;
  return Math.max(percentage, 1.5);
};
</script>

<style scoped>
.bar-chart {
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  width: 100%;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  margin-bottom: 1.5rem;
}

.chart-title {
  margin: 0;
  color: #1f2937;
  font-size: 1.125rem;
  font-weight: 600;
}

.subtitle {
  color: #6b7280;
  font-size: 0.875rem;
}

.legend {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: #6b7280;
  font-size: 0.75rem;
  font-weight: 500;
}

.legend-dot {
  width: 0.6rem;
  height: 0.6rem;
  border-radius: 50%;
  display: inline-block;
}

.primary-dot {
  background: v-bind(color);
}

.secondary-dot {
  background: v-bind(secondaryColor);
}

.rows {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.row {
  display: grid;
  grid-template-columns: 8.5rem 1fr 4rem;
  align-items: center;
  gap: 0.75rem;
}

.row-label {
  color: #374151;
  font-size: 0.8125rem;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.row-track {
  display: flex;
  background: #f3f4f6;
  border-radius: 4px;
  height: 1.1rem;
  overflow: hidden;
}

.row-bar {
  height: 100%;
  transition: width 0.3s ease;
  cursor: pointer;
}

.row-bar.primary {
  background: v-bind(color);
  border-radius: 4px 0 0 4px;
}

.row-bar.secondary {
  background: v-bind(secondaryColor);
  border-radius: 0 4px 4px 0;
}

.row-bar:only-child {
  border-radius: 4px;
}

.row-bar:hover {
  opacity: 0.85;
}

.row-count {
  font-weight: 700;
  font-size: 0.8125rem;
  text-align: right;
  white-space: nowrap;
}

.count-primary {
  color: v-bind(color);
}

.count-secondary {
  color: v-bind(secondaryColor);
}

.count-sep {
  color: #d1d5db;
  margin: 0 0.15rem;
}

.no-data {
  text-align: center;
  color: #6b7280;
  padding: 2rem;
  font-style: italic;
}

.no-data p {
  margin: 0;
}

@media (max-width: 768px) {
  .bar-chart {
    padding: 1rem;
  }

  .row {
    grid-template-columns: 6rem 1fr 3.25rem;
    gap: 0.5rem;
  }

  .row-label {
    font-size: 0.75rem;
  }
}
</style>
